import { useState, useCallback, useEffect } from 'react';
import { useModal, ButtonColor, ButtonVariant } from '@ampol/design-system';
import { useDispatch } from 'react-redux';
import { trackCustomEvent } from '../../../../utilities/app-insights';
import { activateCard } from '../../../../actions/cardActions';
import { activateAllCards, getCashCardDetails } from '../../../../actions/cashActions';
import type { AmpolCashOrdersContext } from '../../../../selectors/ampolCashOrdersSelectors';

/**
 * Ampol Cash Modals Hook
 *
 * Manages modal state, selections, and activation logic for Ampol Cash Orders.
 *
 * @param context - Redux context from memoized selector
 * @param selectedOrder - Selected order state
 * @param AmpolCashOrderDetailsModal - Order details modal component
 * @param AmpolCashCardDetailsModal - Card details modal component
 * @returns Modal state, configurations, and handlers
 */
interface UseAmpolCashModalsProps {
  context: AmpolCashOrdersContext;
  selectedOrder: any;
  setSelectedOrder: (order: any) => void;
  AmpolCashOrderDetailsModal: React.ComponentType<any>;
  AmpolCashCardDetailsModal: React.ComponentType<any>;
}

export const useAmpolCashModals = ({
  context,
  selectedOrder,
  setSelectedOrder,
  AmpolCashOrderDetailsModal,
  AmpolCashCardDetailsModal,
}: UseAmpolCashModalsProps) => {
  const dispatch = useDispatch();

  // Local state
  const [selectedCardDetails, setSelectedCardDetails] = useState<any>(null);
  const [isCardDetailsClicked, setIsCardDetailsClicked] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Destructure context for cleaner access
  const {
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    canReadWriteCash,
    activateAllEnabled,
    activateAllCardsLoading,
    activateAllCardsLoaded,
    activateCardSingleIsLoading,
  } = context;

  // Handlers
  const openCardDetails = useCallback((cardDetails: any) => {
    setSelectedCardDetails({ ...cardDetails });
  }, []);

  const closeCardDetails = useCallback(() => {
    setIsCardDetailsClicked(false);
  }, []);

  const activateSingleCard = useCallback(() => {
    if (!selectedCardDetails) return;

    trackCustomEvent(
      'Click Activate Single',
      loggedInUser,
      assignedAccountNumber,
      'Click Activate button on Cash Card Detail modal.',
      { selectedOrder: selectedOrder?.orderNumber, selectedCard: selectedCardDetails?.cardNumber }
    );
    dispatch(activateCard(selectedCardDetails.cardNumber, loggedInUser.userId, assignedGrandfatherClientNumber));
  }, [
    selectedCardDetails,
    selectedOrder,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    dispatch,
  ]);

  const handleActivateAllClick = useCallback(() => {
    if (!activateAllEnabled || activateAllCardsLoading || activateAllCardsLoaded) {
      return;
    }

    trackCustomEvent(
      'Click Activate All',
      loggedInUser,
      assignedAccountNumber,
      'Click Activate button on Cash Order Detail modal.',
      { selectedOrder: selectedOrder?.orderNumber }
    );
    window.dataLayer.push({
      event: 'CP_Cash_Order_Details_Activate_All',
      button_click: true,
    });
    dispatch(
      activateAllCards(selectedOrder?.orderNumber, assignedGrandfatherClientNumber, assignedGrandfatherClientNumber)
    );
  }, [
    activateAllEnabled,
    activateAllCardsLoading,
    activateAllCardsLoaded,
    selectedOrder,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    dispatch,
  ]);

  const handleOrderClick = useCallback(() => {
    if (assignedGrandfatherClientNumber) {
      setShowOrderModal(true);
      trackCustomEvent(
        'Create New Order Button',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Order button to place an order.'
      );
      window.dataLayer.push({
        event: 'CP_Cash_Order_Start',
        button_click: true,
      });
    }
  }, [assignedGrandfatherClientNumber, loggedInUser, assignedAccountNumber]);

  // Order Details Modal configuration
  const orderDetailsModal = useModal({
    id: 'cash-order-details',
    title: 'Order details',
    subtitle: `Order number ${selectedOrder?.orderNumber}`,
    content: (
      <AmpolCashOrderDetailsModal
        orderNumber={selectedOrder?.orderNumber}
        grandParentAccountNumber={assignedGrandfatherClientNumber}
        contractIdentifier={assignedGrandfatherClientNumber}
        openCardDetails={openCardDetails}
        canReadWrite={canReadWriteCash}
        selectedOrderDetails={selectedOrder}
      />
    ),
    variant: 'triple',
    actions: [
      {
        label: 'Close',
        onClick: () => orderDetailsModal.close(),
      },
    ],
    color: ButtonColor.Blue,
    topActions: canReadWriteCash && [
      {
        label: 'Activate All',
        onClick:
          !activateAllEnabled || activateAllCardsLoading || activateAllCardsLoaded ? undefined : handleActivateAllClick,
        inactive: !activateAllEnabled || activateAllCardsLoading || activateAllCardsLoaded,
      },
    ],
    onClose: () => {
      window.dataLayer.push({
        event: 'CP_Cash_Order_Details_Close',
        button_click: true,
      });
      trackCustomEvent(
        'Close Cash Order Detail Modal',
        loggedInUser,
        assignedAccountNumber,
        'Cash Order Detail Modal closed.',
        { selectedOrder: selectedOrder?.orderNumber }
      );
    },
  });

  // Card Details Modal configuration
  const cardDetailsModal = useModal({
    id: 'cash-card-details',
    title: 'Card details',
    subtitle: `Order number ${selectedOrder?.orderNumber}`,
    content: <AmpolCashCardDetailsModal cardDetails={selectedCardDetails} />,
    variant: 'triple',
    actions: [
      {
        label: isCardDetailsClicked ? 'Close' : 'Back',
        onClick: () => {
          cardDetailsModal.close();
          closeCardDetails();
        },
        variant: isCardDetailsClicked ? ButtonVariant.Primary : ButtonVariant.Secondary,
      },
    ],
    color: ButtonColor.Blue,
    topActions: canReadWriteCash && [
      {
        label: 'Activate',
        onClick:
          selectedCardDetails?.status === 'Inactive' && !activateCardSingleIsLoading ? activateSingleCard : undefined,
        inactive: selectedCardDetails?.status !== 'Inactive' || activateCardSingleIsLoading,
      },
    ],
    onClose: () => {
      trackCustomEvent(
        'Close Cash Card Detail Modal',
        loggedInUser,
        assignedAccountNumber,
        'Cash Card Detail Modal closed.',
        { selectedOrder: selectedOrder?.orderNumber, selectedCard: selectedCardDetails?.cardNumber }
      );
      setIsCardDetailsClicked(false);
    },
  });

  // Effect: Open order details modal when order is selected (not from card click)
  useEffect(() => {
    if (selectedOrder && !isCardDetailsClicked) {
      trackCustomEvent(
        'Open Cash Order Details Modal',
        loggedInUser,
        assignedAccountNumber,
        'Select order from order list to open Order Details Modal.',
        { selectedOrder: selectedOrder?.orderNumber }
      );
      orderDetailsModal.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder, isCardDetailsClicked]);

  // Effect: Open card details modal and fetch card data when card is selected
  useEffect(() => {
    if (selectedCardDetails) {
      cardDetailsModal.open();
      dispatch(
        getCashCardDetails(
          selectedOrder?.orderNumber,
          assignedGrandfatherClientNumber,
          assignedGrandfatherClientNumber,
          selectedCardDetails?.cardNumber
        )
      );
      trackCustomEvent(
        'Open Cash Card Details Modal',
        loggedInUser,
        assignedAccountNumber,
        'Select cash card from order details modal to open Card Details Modal.',
        { selectedOrder: selectedOrder?.orderNumber, selectedCard: selectedCardDetails?.cardNumber }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCardDetails]);

  return {
    // State
    selectedCardDetails,
    isCardDetailsClicked,
    showOrderModal,

    // Setters
    setSelectedCardDetails,
    setIsCardDetailsClicked,
    setShowOrderModal,

    // Modal configurations
    orderDetailsModal,
    cardDetailsModal,

    // Handlers
    openCardDetails,
    closeCardDetails,
    activateSingleCard,
    handleActivateAllClick,
    handleOrderClick,
  };
};
